import { NumberField as NumberFieldPrimitive } from "@base-ui/react/number-field";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import * as React from "react";

import { cn } from "@beweegbaas/ui/lib/utils";
import { Button } from "./button";
import { FieldDescription, FieldError, FieldLabel } from "./field";
import { InputGroup, InputGroupInput } from "./input-group";

const NumberFieldRoot = NumberFieldPrimitive.Root;

interface NumberFieldProps extends NumberFieldPrimitive.Root.Props {
  label?: string;
  description?: string;
  errorMessage?: string | React.ReactNode;
  placeholder?: string;
  withStepper?: boolean;
  fieldGroupClassName?: string;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  inputGroupStyle?: React.CSSProperties;
  ariaInvalid?: boolean;
}

function NumberField({
  label,
  description,
  errorMessage,
  placeholder,
  withStepper = true,
  fieldGroupClassName,
  inputClassName,
  className,
  value,
  inputStyle,
  inputGroupStyle,
  "aria-invalid": ariaInvalid,
  // Without an explicit locale Base UI follows the runtime: the server and an English browser
  // would format and parse 4,5 with a decimal point.
  locale = "nl-NL",
  ...props
}: NumberFieldProps) {
  // `null` must reach Base UI as-is: it is the controlled "empty" value (rendered as "").
  // Mapping it to `undefined` made empty fields mount UNCONTROLLED — Base UI locks that in on
  // first render, so a value arriving later (e.g. a router loader revalidation) never showed.
  const isReadOnly = props.readOnly;
  const inputId = React.useId();

  return (
    <NumberFieldRoot
      className={cn("group flex flex-col gap-1", className)}
      value={value}
      locale={locale}
      {...props}
    >
      {label && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
      <NumberFieldGroup className={fieldGroupClassName} style={inputGroupStyle}>
        <NumberFieldInput
          id={inputId}
          placeholder={placeholder}
          className={inputClassName}
          style={inputStyle}
          aria-invalid={ariaInvalid}
        />
        {withStepper && !isReadOnly && (
          <div className="border-border flex flex-col border-l">
            <NumberFieldIncrement className={"h-auto"}>
              <IconChevronUp aria-hidden className="w-4" />
            </NumberFieldIncrement>
            <NumberFieldDecrement className={"h-auto"}>
              <IconChevronDown aria-hidden className="w-4" />
            </NumberFieldDecrement>
          </div>
        )}
      </NumberFieldGroup>
      {description && <FieldDescription>{description}</FieldDescription>}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </NumberFieldRoot>
  );
}

function NumberFieldGroup({ className, ...props }: NumberFieldPrimitive.Group.Props) {
  return (
    <NumberFieldPrimitive.Group
      render={<InputGroup />}
      data-slot="number-field-group"
      className={cn(
        // Legacy-specific styling (background gradients, etc.)
        `blue:bg-white/10 blue:text-white bg-white bg-linear-to-br from-white
        to-black/2 shadow-sm dark:bg-none dark:bg-black/40`,
        className,
      )}
      {...props}
    />
  );
}

function NumberFieldInput({ className, ...props }: NumberFieldPrimitive.Input.Props) {
  return (
    <NumberFieldPrimitive.Input render={<InputGroupInput />} className={cn(className)} {...props} />
  );
}

function NumberFieldIncrement({ className, ...props }: NumberFieldPrimitive.Increment.Props) {
  return (
    <NumberFieldPrimitive.Increment
      render={
        <Button
          variant="ghost"
          size="icon-xs"
          className="hover:bg-muted data-[pressed]:bg-muted group-data-[disabled]:text-muted-foreground my-[1px] grow
            cursor-default rounded-none
            px-0.5
            text-black/50 data-[disabled]:cursor-not-allowed
            data-[disabled]:bg-black/5 data-[disabled]:text-black/20"
        />
      }
      data-slot="number-field-increment"
      className={cn(className)}
      {...props}
    />
  );
}

function NumberFieldDecrement({ className, ...props }: NumberFieldPrimitive.Decrement.Props) {
  return (
    <NumberFieldPrimitive.Decrement
      render={
        <Button
          variant="ghost"
          size="icon-xs"
          className="hover:bg-muted data-[pressed]:bg-muted group-data-[disabled]:text-muted-foreground my-[1px] grow
            cursor-default rounded-none
            px-0.5
            text-black/50 data-[disabled]:cursor-not-allowed
            data-[disabled]:bg-black/5 data-[disabled]:text-black/20"
        />
      }
      data-slot="number-field-decrement"
      className={cn(className)}
      {...props}
    />
  );
}

export {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldRoot,
};
